import React, {Component, ReactElement} from 'react'
import NatTokenRepo, {RegisterTokenPasswordRequest} from '../token/repository/NatTokenRepo'
import NatStepComponent, {DIspStepInfo, Step} from '../common/resident/NatStepComponent'
import {StyledI} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {
    ContentsMainTitle,
    ContentsSubTitleInner,
    ContentsSubTitleWrapper,
    IconFont,
    ParagraphText,
    WideMainContainer,
} from '../../../styledComponents/ContentsArea'
import {
    DisplayBlockDiv,
    DisplayItemInputLabelRequired,
    DisplayItemInputTitleRequired,
    DisplayItemTextInput,
    FieldErrorLabel,
} from '../../../styledComponents/DisplayItem'
import {CenterPlainLinkStyledButton, PrimaryStyledButton} from '../../../styledComponents/Button'
import {Nullable} from '../../../app/TypeHelpers'
import {ErrorResponseBody} from '../../../network/response/ErrorResponseBody'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {createResidentPage, ResidentPagePropsSet} from '../../../components/shared/hoc/createResidentPage'
import {NatAsyncCallFunction} from '../../common/util/Natutil'
import {NatPostTokenPassword} from '../token/repository/NatPostTokenPassword'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {Location} from 'react-router-dom'


interface FieldsError {
    mailAddress: Error[]
}

interface Error {
    message: string
    hasError: boolean
}

type Field = 'mailAddress'

interface Props {
    tokenRepo: NatTokenRepo,
    location: Location,
}

interface States {
    inputMailAddressValue: string
    sendMailButtonDispFlg: boolean
    dIspStepInfo: DIspStepInfo
    fieldsError: Partial<FieldsError>,
    isLoadCompleted: boolean
}

class NatResidentPasswordResetMailAddressInputPage extends Component<Props & ResidentPagePropsSet, States> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        this.state = {
            inputMailAddressValue: '',
            sendMailButtonDispFlg: false,
            dIspStepInfo: {mailAddressInput: true, mailSend: true},
            fieldsError: {},
            isLoadCompleted: false,
        }

        this.sendMailButtonClick = this.sendMailButtonClick.bind(this)
        this.backLoginButtonClick = this.backLoginButtonClick.bind(this)
        this.onChangeMailAddress = this.onChangeMailAddress.bind(this)
    }

    componentDidMount(): void {
        this.setState({isLoadCompleted: true})
    }

    onChangeMailAddress(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({inputMailAddressValue: event.target.value})
        if (event.target.value) {
            this.setState({sendMailButtonDispFlg: true})
        } else {
            this.setState({sendMailButtonDispFlg: false})
        }
    }

    // 送信する
    async sendMailButtonClick(): Promise<void> {
        await this.clearMessage()

        await this.props.eventEmitter.showLoading()
        try {
            // トークン登録&メール送信
            const registerTokenRequest: RegisterTokenPasswordRequest = {
                mailAddress: this.state.inputMailAddressValue,
            }
            const ret: Nullable<NatPostTokenPassword> = await NatAsyncCallFunction(
                this.props.tokenRepo.registerTokenPassword.bind(
                    this.props.tokenRepo, registerTokenRequest), this.props.navigate)

            if (ret) {
                // パスワード再設定メール送信完了へ遷移
                this.props.navigate(
                    `/resident/passwordResetMailAddressComplete`,
                    {state: {mailAddress: this.state.inputMailAddressValue}},
                )
            }
        } catch (HttpError) {
            const errorResponseBody: Nullable<ErrorResponseBody> = (HttpError as BaseHttpError).responseBody
            if (errorResponseBody !== null) {
                errorResponseBody.requestItemErrors.forEach((item: RequestItemError) => {
                    if (item.fields) {
                        item.fields.forEach((field) => {
                            this.setFieldErrorMessage(field, item.message)
                        })
                    }
                    if (item.resultCode) {
                        this.setFieldErrorMessage(item.resultCode, item.message)
                    }
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    };

    // ログインページへ
    backLoginButtonClick(): void {
        this.props.navigate(
            `/resident/login`,
        )
    };

    // メッセージ設定（フィールド）
    setFieldErrorMessage(field: string, message: string): void {
        if (field === 'mailAddress') {
            let error: Error[] | undefined = this.state.fieldsError[field]
            if (error === undefined) {
                error = [{message: message, hasError: true}]
            } else {
                error.push({message: message, hasError: true})
            }


            this.setState(({fieldsError}) => (
                {
                    fieldsError: {
                        ...fieldsError,
                        [field]: error,
                    },
                }))
        }
    }

    // メッセージ取得（フィールド）
    getFieldErrorMessage(fieldId: Field): Nullable<ReactElement> {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        if (field !== undefined) {
            return (
                <>
                    {field.map((error, index) => (
                        <FieldErrorLabel key={index}>
                            {error.message}
                        </FieldErrorLabel>
                    ))}
                </>
            )
        } else {
            return null
        }
    }

    // フィールドエラー状態取得
    getFieldErrorStatus(fieldId: Field): boolean {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        return !!(field !== undefined && field[0] !== undefined && field[0].hasError)
    }

    // メッセージクリア
    clearMessage(): void {
        this.setState({fieldsError: {}})
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <ContentsMainTitle><TitleIcon/>パスワードを忘れた場合</ContentsMainTitle>
                <NatStepComponent
                    dIspStepInfo={this.state.dIspStepInfo}
                    nowStep={Step.MAIL_ADDRESS_INPUT}
                />
                <ContentsSubTitleWrapper>
                    <ContentsSubTitleInner>
                        メールアドレス入力
                    </ContentsSubTitleInner>
                </ContentsSubTitleWrapper>
                <WideMainContainer>
                    <BlockSpacer height={'15px'}/>
                    <ParagraphText>
                        素敵ネットに登録されているメールアドレスを入力してください。
                    </ParagraphText>
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>メールアドレス</DisplayItemInputTitleRequired>
                            <DisplayItemTextInput
                                type="text"
                                name="mailAddress"
                                maxLength={100}
                                onChange={this.onChangeMailAddress}
                                hasError={this.getFieldErrorStatus('mailAddress')}
                                autoFocus={true}
                            />
                        </DisplayItemInputLabelRequired>
                    </DisplayBlockDiv>
                    {this.getFieldErrorMessage('mailAddress')}
                    <BlockSpacer height={'5px'}/>
                </WideMainContainer>
                <BlockSpacer height={'30px'}/>
                <PrimaryStyledButton
                    onClick={this.sendMailButtonClick}
                    disabled={!this.state.sendMailButtonDispFlg}>
                    送信する
                </PrimaryStyledButton>
                <BlockSpacer height={'30px'}/>
                <CenterPlainLinkStyledButton onClick={this.backLoginButtonClick}>
                    ログイン画面へ戻る
                </CenterPlainLinkStyledButton>
                <BlockSpacer height={'30px'}/>
            </>
        )
    }
}

export default createResidentPage(NatResidentPasswordResetMailAddressInputPage)

// タイトルアイコン
const TitleIcon: StyledI = styled.i`
  ${IconFont};
  font-size: 6.0rem;
  padding-right: 15px;

  &::before {
      content: "\f138";
  }
`
